import { AlertModal, DeleteButton, Loader, Spinner } from '@/components/Common';
import { Input } from '@/components/ui';
import { CACHE_TIME } from '@/constants';
import { trpc } from '@/data-loaders';
import { useHandleError, useHandleSuccess } from '@/hooks';
import { useAwmsStore } from '@/store';
import { Button } from '@curtin-dxp/curtin-ui';
import { CircleCheckBig } from 'lucide-react';
import { ChangeEvent, useEffect, useRef, useState } from 'react';

const tableCellStyle = 'border border-gray-300 px-4 py-2';

export const RemoveUnits = () => {
  const [enteredUDC, setEnteredUDC] = useState<string>('');
  const [validateUDCButtonClicked, setValidateUDCButtonClicked] = useState<number>(0);
  const [validUDC, setValidUDC] = useState<boolean>(false);
  const [UDCToValidate, setUDCToValidate] = useState<string>('');
  const [studyPeriodToDelete, setStudyPeriodToDelete] = useState<{
    unitWorkloadId: number;
    unitName: string;
    studyPeriodName: string;
  } | null>(null);
  const sendToastRemoveStudyPeriodSuccess = useHandleSuccess({ title: 'Remove unit from study period' });
  const activeYear = useAwmsStore((state) => state.activeYear);
  const handleError = useHandleError();
  const lastErrorRef = useRef<any>(null);


  const {
    data: unitDetails = [],
    refetch: refetchUnitDetails,
    isFetching: isFetchingUnitDetails,
    error: validateUDCError,
  } = trpc.unit.getUnitWorkloadAvailabilityByUDC.useQuery(
    { udc: UDCToValidate, year: activeYear },
    {
      cacheTime: CACHE_TIME,
      enabled: validUDC,
      retry: false,
      refetchOnWindowFocus: false,
    },
  );
  
  const { mutate: removeUnitWorkloadMutation } = trpc.unit.removeUnitWorkloadById.useMutation({
    onSuccess: async (_data, { unitWorkloadId }) => {
      sendToastRemoveStudyPeriodSuccess();
      setStudyPeriodToDelete(null);
      // A refetch after removing the last study period would throw NOT_FOUND, so reset instead.
      const hasRemainingStudyPeriods = unitDetails.some((row) => row.unitWorkloadId !== unitWorkloadId);
      if (hasRemainingStudyPeriods) {
        await refetchUnitDetails();
      } else {
        setValidUDC(false);
        setUDCToValidate('');
      }
    },
    onError: (error) => {
      setStudyPeriodToDelete(null);
      handleError([error]);
    },
  });

  const campusesInStudyPeriodToDelete = studyPeriodToDelete
    ? unitDetails
        .filter((row) => row.unitWorkloadId === studyPeriodToDelete.unitWorkloadId)
        .map((row) => row.campusName)
    : [];

  useEffect(() => {
    if (UDCToValidate && unitDetails.length > 0) {
      setValidUDC(true);
    }
  }, [UDCToValidate, unitDetails]);

  useEffect(() => {
    if (validateUDCError && lastErrorRef.current !== validateUDCError) {
      setValidUDC(false);
      handleError([validateUDCError]);
      lastErrorRef.current = validateUDCError;
    }
  }, [handleError, validateUDCError]);

  useEffect(() => {
    if (UDCToValidate) {
      refetchUnitDetails();
    }
  }, [UDCToValidate, validateUDCButtonClicked]);

  const handleUnitInputChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setValidUDC(false);
    setUDCToValidate('');
    setEnteredUDC(event.target.value);
  };

  const handleValidateUnitUDC = () => {
    setValidateUDCButtonClicked(validateUDCButtonClicked + 1);
    setUDCToValidate(enteredUDC);
  };

  if (isFetchingUnitDetails) {
    return <Loader />;
  }

  return (
    <div>
      <AlertModal
        title="Remove this unit from the study period?"
        description={
          studyPeriodToDelete
            ? `This will permanently delete this study period/campus record for the unit.`
            : ''
        }
        confirmAction={() => {
          if (studyPeriodToDelete) {
            removeUnitWorkloadMutation({
              unitWorkloadId: studyPeriodToDelete.unitWorkloadId,
              year: activeYear,
            });
          }
        }}
        open={studyPeriodToDelete !== null}
        cancelAction={() => {
          setStudyPeriodToDelete(null);
        }}
      />
      <div className="ml-8 mt-10">
        <div className="flex w-[400px] justify-between">
          <div className="relative">
            <Input
              type="text"
              value={enteredUDC}
              className="w-[180px] border-secondary-grey-04 pr-10 text-b2"
              onChange={handleUnitInputChange}
              placeholder="Enter UDC"
            />
            {validUDC && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-green-600">✔️</span>}
          </div>
          <Button
            variant="outline-secondary"
            className="h-10 border-black bg-black text-white hover:bg-transparent hover:text-black focus:outline-none focus-visible:ring-1
              focus-visible:ring-inset focus-visible:ring-white focus-visible:ring-offset-0"
            aria-label="Validate UDC"
            onClick={handleValidateUnitUDC}
          >
            Validate UDC
            <CircleCheckBig className="ml-[10px] h-6 w-6" />
          </Button>
        </div>
        
        {validUDC && (
          <div className="flex h-[150px] flex-col items-start justify-between">
            <div className="flex flex-col items-start justify-center gap-3">
              <div className="mt-4 flex justify-normal gap-3">
                <div className="text-base font-semibold leading-6 text-[#1A1A1A]">{unitDetails[0]?.unitName} </div>
              </div>
              <div className="text-base font-normal leading-6">{unitDetails[0]?.schoolName}</div>
            </div>
          <div className="mt-4 flex flex-col gap-2">
            <div className="mt-4">
              <table className="table-auto">
                <thead className="table-header-group">
                  <tr>
                    <th className={tableCellStyle}>Faculty</th>
                    <th className={tableCellStyle}>School</th>
                    <th className={tableCellStyle}>School_Org_Unit_Code</th>
                    <th className={tableCellStyle}>Unit_Name</th>
                    <th className={tableCellStyle}>Year</th>
                    <th className={tableCellStyle}>Study_Period</th>
                    <th className={tableCellStyle}>Campus</th>
                    <th className={tableCellStyle}>Study_Mode</th>
                    <th className={tableCellStyle}>Student Enrolments</th>
                    <th className={tableCellStyle}></th>
                  </tr>
                </thead>
                <tbody>
                  {unitDetails.map((row) => (
                    <tr key={`${row.unitWorkloadId}-${row.campusId}`}>
                      <td className={tableCellStyle}>{row.facultyName}</td>
                      <td className={tableCellStyle}>{row.schoolName}</td>
                      <td className={tableCellStyle}>{row.schoolOrgCode}</td>
                      <td className={tableCellStyle}>{row.unitName}</td>
                      <td className={tableCellStyle}>{row.year}</td>
                      <td className={tableCellStyle}>{row.studyPeriodName}</td>
                      <td className={tableCellStyle}>{row.campusName}</td>
                      <td className={tableCellStyle}>{row.studyModeName}</td>
                      <td className={tableCellStyle}>{row.studentEnrolments}</td>
                      <td className={`${tableCellStyle} text-center`}>
                        <DeleteButton
                          label="Remove unit from this study period"
                          onClick={() =>
                            setStudyPeriodToDelete({
                              unitWorkloadId: row.unitWorkloadId,
                              unitName: row.unitName,
                              studyPeriodName: row.studyPeriodName,
                            })
                          }
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
        </div>
          </div>
        )}
      </div>
    </div>
  );
};
